/**
 * modelo.js — El muñeco que lleva puesta la skin.
 *
 * Antes el cuerpo de tercera persona se construía a mano dentro de main.js con
 * los colores escritos a pelo (torso cian, cabeza tostada), y el selector de
 * skins no estaba conectado a nada: elegir una no cambiaba absolutamente nada
 * en pantalla. Esto es lo que faltaba para que elegir signifique algo.
 *
 * Cada parte es una caja con las UV recolocadas sobre la skin de 64×64. La
 * vuelta de canvas (y hacia abajo) a UV (v hacia arriba) se hace aquí y solo
 * aquí.
 */
import * as THREE from 'three';
import { PARTES, CAPA2, CARAS, TAM, ESCALA } from './layout.js';
import { lienzoDeSkin } from './pintor.js';

/* El orden de los grupos de BoxGeometry es +X, -X, +Y, -Y, +Z, -Z. El modelo
   mira hacia +Z, así que su derecha —la del personaje— es -X. */
const ORDEN = ['izquierda', 'derecha', 'arriba', 'abajo', 'frente', 'espalda'];

/** Caja con las UV puestas sobre el recorte que le toca de la skin. */
function caja(ancho, alto, fondo, def, { inflar = 0 } = {}) {
  const g = new THREE.BoxGeometry(
    (ancho + inflar) * ESCALA, (alto + inflar) * ESCALA, (fondo + inflar) * ESCALA,
  );
  const uv = g.attributes.uv;
  ORDEN.forEach((cara, i) => {
    const [x, y, w, h] = def[cara];
    const u0 = x / TAM, u1 = (x + w) / TAM;
    // v va al revés que el canvas: la fila 0 del lienzo es v = 1
    const v0 = 1 - y / TAM, v1 = 1 - (y + h) / TAM;
    /* Las caras de un costado se ven en espejo respecto a la otra: si no se
       voltea, el dibujo de la derecha aparece invertido. */
    const [a, b] = cara === 'izquierda' || cara === 'espalda' ? [u1, u0] : [u0, u1];
    uv.setXY(i * 4 + 0, a, v0);
    uv.setXY(i * 4 + 1, b, v0);
    uv.setXY(i * 4 + 2, a, v1);
    uv.setXY(i * 4 + 3, b, v1);
  });
  uv.needsUpdate = true;
  return g;
}

/** Textura de la skin: sin filtrado, o los píxeles se emborronan. */
export function texturaDeSkin(skin) {
  const t = new THREE.CanvasTexture(lienzoDeSkin(skin));
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  if ('colorSpace' in t) t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/**
 * Construye el muñeco. Devuelve el grupo y los pivotes que hay que mover para
 * animarlo — los brazos y las piernas giran desde el hombro y la cadera, no
 * desde su centro, o el paso parece el de una marioneta rota.
 */
export function crearModelo(skin) {
  const tex = texturaDeSkin(skin);
  const mat = new THREE.MeshLambertMaterial({ map: tex });
  const mat2 = new THREE.MeshLambertMaterial({
    map: tex, transparent: true, alphaTest: 0.5, side: THREE.DoubleSide,
  });

  const grupo = new THREE.Group();
  const pieza = (def, def2, ancho, alto, fondo) => {
    const g = new THREE.Group();
    const cuerpo = new THREE.Mesh(caja(ancho, alto, fondo, def), mat);
    /* Sin esto el muñeco flota sin sombra y se nota muchísimo: es lo primero
       que delata que las sombras son de mentira. */
    cuerpo.castShadow = true;
    g.add(cuerpo);
    // la capa 2 va medio píxel por fuera; con alphaTest lo transparente no tapa
    g.add(new THREE.Mesh(caja(ancho, alto, fondo, def2, { inflar: 1 }), mat2));
    return g;
  };

  // cabeza: pivota en el cuello
  const cabeza = pieza(PARTES.cabeza, CAPA2.cabeza, 8, 8, 8);
  cabeza.position.y = 24 * ESCALA;
  cabeza.children.forEach((m) => { m.position.y = 4 * ESCALA; });
  grupo.add(cabeza);

  /* Las alturas son las canónicas, con el origen en los pies y 16 px = 1
     bloque: piernas 0–12, torso 12–24, cabeza 24–32 y los brazos colgando del
     hombro (22) hasta el 10, un poco por debajo del torso.

     El torso NO lleva desplazamiento: es la única pieza que no pivota. Con el
     -6 que tenía antes quedaba seis píxeles bajo, y entre su borde de arriba y
     la barbilla se abría un hueco de casi medio bloque: la cabeza flotaba y
     los brazos parecían salir del aire. */
  const torso = pieza(PARTES.torso, CAPA2.torso, 8, 12, 4);
  torso.position.y = 18 * ESCALA;
  grupo.add(torso);

  const brazo = (der) => {
    const g = pieza(
      der ? PARTES.brazoDerecho : PARTES.brazoIzquierdo,
      der ? CAPA2.brazoDerecho : CAPA2.brazoIzquierdo, 4, 12, 4,
    );
    g.position.set((der ? -6 : 6) * ESCALA, 22 * ESCALA, 0);
    g.children.forEach((m) => { m.position.y = -6 * ESCALA; });   // pivote en el hombro
    grupo.add(g);
    return g;
  };
  const pierna = (der) => {
    const g = pieza(
      der ? PARTES.piernaDerecha : PARTES.piernaIzquierda,
      der ? CAPA2.piernaDerecha : CAPA2.piernaIzquierda, 4, 12, 4,
    );
    g.position.set((der ? -2 : 2) * ESCALA, 12 * ESCALA, 0);
    g.children.forEach((m) => { m.position.y = -6 * ESCALA; });   // pivote en la cadera
    grupo.add(g);
    return g;
  };

  const partes = {
    cabeza, torso,
    brazoDer: brazo(true), brazoIzq: brazo(false),
    piernaDer: pierna(true), piernaIzq: pierna(false),
  };

  grupo.userData = { partes, materiales: [mat, mat2], textura: tex };
  return grupo;
}

/** Cambia la skin sin rehacer la geometría: solo se repinta la textura. */
export function vestirModelo(grupo, skin) {
  const { materiales, textura } = grupo.userData;
  textura.dispose();
  const nueva = texturaDeSkin(skin);
  for (const m of materiales) { m.map = nueva; m.needsUpdate = true; }
  grupo.userData.textura = nueva;
}

/**
 * El paso. `avance` es cuánto se ha movido (para la fase) y `andando` si toca
 * mover las piernas; quieto, todo vuelve a cero suavemente.
 */
export function animarModelo(grupo, { fase, andando, enElAire }) {
  const p = grupo.userData.partes;
  const amp = andando ? 0.85 : 0;
  const s = Math.sin(fase) * amp;
  p.piernaDer.rotation.x = s;
  p.piernaIzq.rotation.x = -s;
  p.brazoDer.rotation.x = -s * 0.75;
  p.brazoIzq.rotation.x = s * 0.75;
  // en el aire los brazos suben: se lee al instante que está saltando
  const aire = enElAire ? -0.7 : 0;
  p.brazoDer.rotation.z = aire * 0.35;
  p.brazoIzq.rotation.z = -aire * 0.35;
  p.brazoDer.rotation.x += aire;
  p.brazoIzq.rotation.x += aire;
}
